import React from "react";
import { Capacitor } from "@capacitor/core";
import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonToolbar } from "@ionic/react";
import DataController from "@controllers/DataController";
import ContentContainer from "@components/common/ContentContainer";
import EvidenceFilterDropdown from "./EvidenceFilterDropdown";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";
import {
  CPDActivityFilterOptionsSelector,
  customTagsFilterOptionsSelector,
  evidenceFiltersAtom,
  EvidenceSearchFilter,
  getDraftsFilters,
  getEvidenceDateFilters,
  getGeneralEvidenceFilters,
  programModulesFilterOptionsSelector,
} from "@state/FilterState";
import { IEvidence } from "@interfaces/Interfaces";
import { evidenceAtom } from "@state/State";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { HeaderTitle } from "parafolio-components";

type Props = {
  visible: boolean;
  closeModal: () => void;
};

const EvidenceFilterModal = (props: Props) => {
  const [filters, setFilters] = useRecoilState<EvidenceSearchFilter[]>(evidenceFiltersAtom);

  const evidence = useRecoilValueLoadable<IEvidence[]>(evidenceAtom);

  const filterOptionsCPD = useRecoilValue<EvidenceSearchFilter[]>(CPDActivityFilterOptionsSelector);
  const filterProgramModules = useRecoilValue<EvidenceSearchFilter[]>(programModulesFilterOptionsSelector);
  const filterCustomTags = useRecoilValue<EvidenceSearchFilter[]>(customTagsFilterOptionsSelector);

  const toggleSelectedOption = (filter: EvidenceSearchFilter, singleSelect?: boolean) => {
    setFilters((prevFilters) => {
      if (singleSelect) {
        const selectedIndex = prevFilters.findIndex((item) => item.type === filter.type);

        if (selectedIndex !== -1) {
          if (prevFilters[selectedIndex].option === filter.option) {
            return prevFilters.filter((_, i) => i !== selectedIndex);
          } else {
            return [...prevFilters.filter((_, i) => i !== selectedIndex), filter];
          }
        } else {
          return [...prevFilters, filter];
        }
      } else {
        const index = prevFilters.findIndex(
          (_filter) => _filter.type === filter.type && _filter.option === filter.option
        );

        if (index !== -1) {
          return prevFilters.filter((_, i) => i !== index);
        } else {
          return [...prevFilters, filter];
        }
      }
    });
  };

  const hasDraftsEvidence = evidence.contents?.some((evidence: IEvidence) => evidence.draft);

  const filteredEvidence = EvidenceUtils.getFilteredEvidence(filters, evidence.contents ?? []);

  return (
    <IonModal
      id="evidence-filter-modal"
      isOpen={props.visible}
      backdropDismiss
      canDismiss
      className="fullscreen"
      onDidDismiss={props.closeModal}
    >
      <IonHeader
        style={{
          height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
        }}
      >
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{ maxWidth: DataController.isWeb() ? 980 : undefined }}
        >
          <IonButtons slot="start">
            <IonButton
              mode="ios"
              className="cancelButton"
              onClick={props.closeModal}
            >
              {"Close"}
            </IonButton>
          </IonButtons>
          <HeaderTitle>{"Filter"}</HeaderTitle>
          {filters.length > 0 && (
            <IonButtons slot="end">
              <IonButton
                mode="ios"
                className="cancelButton"
                onClick={() => setFilters([])}
              >
                {"Reset"}
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <ContentContainer className="!pt-3 pb-32">
          <EvidenceFilterDropdown
            filters={filters}
            filterOptions={getGeneralEvidenceFilters}
            title="General"
            toggleSelectedOption={toggleSelectedOption}
          />
          {hasDraftsEvidence && (
            <EvidenceFilterDropdown
              filters={filters}
              filterOptions={getDraftsFilters}
              title="Drafts"
              toggleSelectedOption={toggleSelectedOption}
              singleSelect
            />
          )}
          {filterOptionsCPD.length > 0 && (
            <EvidenceFilterDropdown
              filters={filters}
              filterOptions={filterOptionsCPD}
              title="CPD Activity"
              toggleSelectedOption={toggleSelectedOption}
            />
          )}
          <EvidenceFilterDropdown
            filters={filters}
            filterOptions={getEvidenceDateFilters}
            title="Date Range"
            toggleSelectedOption={toggleSelectedOption}
            singleSelect
          />
          {filterProgramModules.length > 0 && (
            <EvidenceFilterDropdown
              filters={filters}
              filterOptions={filterProgramModules}
              title="Program Modules"
              toggleSelectedOption={toggleSelectedOption}
            />
          )}
          {filterCustomTags.length > 0 && (
            <EvidenceFilterDropdown
              filters={filters}
              filterOptions={filterCustomTags}
              title="Custom Tags"
              toggleSelectedOption={toggleSelectedOption}
            />
          )}
        </ContentContainer>
      </IonContent>
      <div className="flex justify-center bg-white absolute bottom-0 w-full !z-[999999 pb-safe-area-footer">
        <div className="flex items-center h-20 justify-center w-full max-w-app-column pl-safe-area-l pr-safe-area-r">
          <IonButton
            mode="ios"
            disabled={filters.length === 0 || filteredEvidence.length === 0}
            className={`flex w-full max-w-button mb-12px self-center`}
            onClick={() => props.closeModal()}
          >
            <div className="text-white button text-default leading-default text-center tracking-default font-bold">
              {filters.length === 0 ? "Filter" : `Filter ${filteredEvidence.length} evidences`}
            </div>
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default EvidenceFilterModal;
